import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getAlert, findLegislator, takeAction, API } from "@/lib/api";
import { positionBadge } from "@/lib/bills";
import { ArrowLeft, Users, CheckCircle, FileDown, Mail } from "lucide-react";
import { toast } from "sonner";
import { SendToPriestModal } from "@/components/public/SendToPriestModal";
import { useLang } from "@/context/LangContext";
import { useT } from "@/lib/t";

export default function ActionAlertDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = useT();
  const [alert, setAlert] = useState(null);
  const [step, setStep] = useState(1);
  const [zip, setZip] = useState("");
  const [legislators, setLegislators] = useState([]);
  const [selected, setSelected] = useState({});
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sendVia, setSendVia] = useState("mailto"); // mailto | ccc
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(0);
  const [priestModalOpen, setPriestModalOpen] = useState(false);
  const [priestModalKey, setPriestModalKey] = useState(0);

  useEffect(() => {
    getAlert(slug, lang).then(a => {
      setAlert(a);
      setForm(f => ({ ...f, subject: a.default_subject, message: a.default_message }));
      setCount(a.actions_count);
    });
  }, [slug, lang]);

  const lookup = async () => {
    if (!zip.trim()) return toast.error(t("alerts.err_no_zip"));
    try {
      const r = await findLegislator({ zip });
      setLegislators(r.legislators);
      const sel = {}; r.legislators.forEach(l => { sel[l.id] = true; });
      setSelected(sel);
      setStep(2);
    } catch { toast.error(t("alerts.err_lookup_fail")); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const legIds = Object.entries(selected).filter(([_, v]) => v).map(([k]) => k);
    if (legIds.length === 0) return toast.error(t("alerts.err_no_legislator"));
    try {
      const r = await takeAction(slug, {
        name: form.name, email: form.email, zip,
        house_district: legislators.find(l => l.chamber === "House" && selected[l.id])?.district,
        senate_district: legislators.find(l => l.chamber === "Senate" && selected[l.id])?.district,
        subject: form.subject, message: form.message,
        legislator_ids: legIds, send_via_ccc: sendVia === "ccc",
      });
      setCount(r.actions_count);
      if (sendVia === "mailto") {
        const toEmails = legislators.filter(l => selected[l.id]).map(l => l.email).join(",");
        window.location.href = `mailto:${toEmails}?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(form.message)}`;
      }
      setDone(true);
      toast.success(t("alerts.success_recorded"));
    } catch { toast.error(t("alerts.err_submit_fail")); }
  };

  if (!alert) return <div className="max-w-4xl mx-auto px-6 py-24">{t("common.loading")}</div>;

  return (
    <div data-testid="alert-detail">
      <div className="bg-ccc-plum text-ccc-paper py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/take-action/action-alerts" className="text-ccc-gold-light inline-flex items-center gap-1 mb-6 underline decoration-ccc-gold underline-offset-4" style={{ fontSize: "14px" }}>
            <ArrowLeft size={14}/> {t("alerts.all_alerts_link")}
          </Link>
          <span className={`inline-block px-2 py-1 uppercase tracking-wider font-semibold ${positionBadge(alert.position)}`} style={{ fontSize: "11px" }}>{alert.position}</span>
          <h1 className="font-serif text-5xl mt-3 leading-tight">{alert.title}</h1>
          <div className="mt-6 inline-flex items-center gap-2 text-ccc-paper/85" data-testid="action-counter" style={{ fontSize: "15px" }}>
            <Users size={16} className="text-ccc-gold-light"/> <span className="text-ccc-gold-light font-semibold" style={{ fontSize: "20px" }}>{count}</span> {t("alerts.taken_action_before")}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-10">
        <aside className="lg:col-span-2">
          <div className="eyebrow mb-2">{t("alerts.overview")}</div>
          <p className="text-ccc-body leading-relaxed" style={{ fontSize: "16px" }}>{alert.summary}</p>
          <div className="gilded-divider my-6"/>
          <div className="eyebrow mb-2">{t("alerts.talking_points")}</div>
          <ul className="space-y-2 text-ccc-body list-disc pl-5" style={{ fontSize: "15px" }}>
            {alert.talking_points.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
          {alert.bill && (
            <div className="mt-6 border border-ccc-rule p-4 bg-white">
              <div className="eyebrow mb-1" style={{ fontSize: "11px" }}>{t("alerts.related_bill")}</div>
              <Link to={`/legislative-work/${alert.bill.id}`} className="text-ccc-plum font-serif hover:text-ccc-plum-deep" style={{ fontSize: "18px" }}>{alert.bill.number} — {alert.bill.title}</Link>
            </div>
          )}
          <div className="mt-6 border border-ccc-rule p-4 bg-white" data-testid="parish-toolkit">
            <div className="eyebrow mb-2">{t("alerts.parish_toolkit")}</div>
            <div className="text-ccc-body mb-3" style={{ fontSize: "14px" }}>{t("alerts.toolkit_description")}</div>
            <div className="flex flex-col gap-2">
              <a
                href={`${API}/public/action-alerts/${slug}/homily-aid.pdf`}
                target="_blank" rel="noopener noreferrer"
                data-testid="download-homily-aid"
                className="inline-flex items-center gap-2 text-ccc-plum hover:text-ccc-plum-deep border border-ccc-plum/30 hover:border-ccc-plum px-3 py-2"
                style={{ fontSize: "14px" }}
              >
                <FileDown size={14}/> {t("alerts.homily_aid")}
              </a>
              <a
                href={`${API}/public/action-alerts/${slug}/bulletin-insert.pdf`}
                target="_blank" rel="noopener noreferrer"
                data-testid="download-bulletin-insert"
                className="inline-flex items-center gap-2 text-ccc-plum hover:text-ccc-plum-deep border border-ccc-plum/30 hover:border-ccc-plum px-3 py-2"
                style={{ fontSize: "14px" }}
              >
                <FileDown size={14}/> {t("alerts.bulletin_insert")}
              </a>
              <button
                type="button"
                onClick={() => { setPriestModalKey(k => k + 1); setPriestModalOpen(true); }}
                data-testid="open-send-to-priest"
                className="inline-flex items-center gap-2 text-ccc-paper bg-ccc-plum hover:bg-ccc-plum-deep px-3 py-2 font-semibold uppercase tracking-[0.08em] mt-1"
                style={{ fontSize: "13px" }}
              >
                <Mail size={14}/> {t("alerts.email_to_priest")}
              </button>
            </div>
            <div className="text-ccc-muted mt-2 italic" style={{ fontSize: "12px" }}>{t("alerts.imprimatur")}</div>
          </div>
        </aside>

        <div className="lg:col-span-3">
          {done ? (
            <div className="bg-white border border-ccc-rule border-l-4 border-l-ccc-gold p-8" data-testid="action-done">
              <CheckCircle className="text-ccc-plum mb-3" size={36}/>
              <h2 className="font-serif text-3xl text-ccc-plum mb-2">{t("alerts.thank_you")}</h2>
              <p className="text-ccc-body" style={{ fontSize: "16px" }}>{t("alerts.thank_you_body")}</p>
            </div>
          ) : (
            <div className="bg-white border border-ccc-rule p-8">
              <div className="eyebrow mb-2">{t("alerts.step_of_two").replace("{n}", step)}</div>

              {step === 1 && (
                <div data-testid="step-1">
                  <h2 className="font-serif text-3xl text-ccc-plum mb-4">{t("alerts.find_legislators_title")}</h2>
                  <p className="text-ccc-body mb-4" style={{ fontSize: "16px" }}>{t("alerts.find_legislators_body")}</p>
                  <input placeholder={t("alerts.zip_placeholder")} value={zip} onChange={e => setZip(e.target.value)}
                    data-testid="lookup-zip"
                    className="w-full px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum mb-4"
                    style={{ fontSize: "17px" }}/>
                  <button onClick={lookup} data-testid="lookup-btn"
                    className="bg-ccc-plum text-ccc-paper px-6 py-3 uppercase tracking-[0.08em] font-semibold hover:bg-ccc-plum-deep"
                    style={{ fontSize: "15px" }}>
                    {t("alerts.find_legislators_button")}
                  </button>
                </div>
              )}

              {step === 2 && (
                <form onSubmit={submit} data-testid="step-2">
                  <h2 className="font-serif text-3xl text-ccc-plum mb-4">{t("alerts.send_message_title")}</h2>
                  <div className="mb-5">
                    <div className="eyebrow mb-2" style={{ fontSize: "12px" }}>{t("alerts.your_legislators_label")}</div>
                    <div className="space-y-2">
                      {legislators.map(l => (
                        <label key={l.id} className="flex items-start gap-3 border border-ccc-rule p-3 cursor-pointer hover:border-ccc-plum">
                          <input type="checkbox" checked={!!selected[l.id]}
                            onChange={e => setSelected({...selected, [l.id]: e.target.checked})}
                            className="mt-1 accent-ccc-plum"/>
                          <div>
                            <div className="font-medium text-ccc-plum" style={{ fontSize: "15px" }}>{l.name}</div>
                            <div className="text-ccc-muted" style={{ fontSize: "13px" }}>{l.chamber} &middot; {l.district} &middot; {l.email}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <input required placeholder={t("alerts.your_name")} value={form.name}
                      data-testid="send-name"
                      onChange={e => setForm({...form, name: e.target.value})}
                      className="px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
                      style={{ fontSize: "17px" }}/>
                    <input required type="email" placeholder={t("alerts.your_email")} value={form.email}
                      data-testid="send-email"
                      onChange={e => setForm({...form, email: e.target.value})}
                      className="px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
                      style={{ fontSize: "17px" }}/>
                  </div>
                  <input required placeholder={t("alerts.subject")} value={form.subject}
                    onChange={e => setForm({...form, subject: e.target.value})}
                    className="w-full px-3 py-2 border border-ccc-rule mb-3 focus:outline-none focus:border-ccc-plum"
                    style={{ fontSize: "17px" }}/>
                  <textarea required rows={10} value={form.message}
                    onChange={e => setForm({...form, message: e.target.value})}
                    data-testid="send-message"
                    className="w-full px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum font-serif"
                    style={{ fontSize: "16px" }}/>
                  <div className="mt-4 text-ccc-body" style={{ fontSize: "14px" }}>
                    <label className="inline-flex items-center gap-2 mr-4">
                      <input type="radio" checked={sendVia === "mailto"} onChange={() => setSendVia("mailto")}/>
                      {t("alerts.send_via_mail_client")}
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input type="radio" checked={sendVia === "ccc"} onChange={() => setSendVia("ccc")}/>
                      {t("alerts.send_via_ccc")}
                    </label>
                  </div>
                  <button type="submit" data-testid="send-submit"
                    className="mt-6 bg-ccc-plum text-ccc-paper px-6 py-3 font-semibold uppercase tracking-[0.08em] hover:bg-ccc-plum-deep"
                    style={{ fontSize: "15px" }}>
                    {t("alerts.send_message_button")}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
      {priestModalOpen && (
        <SendToPriestModal
          key={priestModalKey}
          onClose={() => setPriestModalOpen(false)}
          alertSlug={slug}
          alertTitle={alert.title}
        />
      )}
    </div>
  );
}
